import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  Image,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  SimpleGrid,
  useToast,
  Textarea,
  Switch,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";

const CodifySpecificationsEditor = ({ codifySpecification, isNew }) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [localSpecification, setLocalSpecification] = useState(codifySpecification || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (codifySpecification) {
      setLocalSpecification(codifySpecification);
    }
  }, [codifySpecification]);

  const handleChange = (field, value) => {
    setLocalSpecification((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = isNew ? "POST" : "PUT";
      const url = isNew
        ? `https://codify.poc.mrc.ai/api/specifications`
        : `https://codify.poc.mrc.ai/api/specifications/${localSpecification._id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...localSpecification,
          updatedDate: new Date().toISOString(),
        }),
      });

      if (!res.ok) throw new Error("Failed to save codify specification");

      const saved = await res.json();
      setLocalSpecification(saved);

      toast({
        title: isNew ? "Specification created" : "Specification updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew) {
        navigate(`/codifyspecifications/${saved._id}`);
      }
    } catch (err) {
      console.error("Error saving codify specification:", err);
      toast({
        title: "Error saving specification",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate("/codify");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mb={4}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <HStack spacing={4} mb={4}>
              <Tooltip label="Back to Codify">
                <IconButton
                  icon={<HiOutlineArrowNarrowLeft />}
                  aria-label="Back"
                  onClick={handleClose}
                />
              </Tooltip>
              <Heading size="lg">
                {isNew ? "New Codify Specification" : "Edit Codify Specification"}
              </Heading>
              <Box flex="1" />
              <Button
                leftIcon={<HiOutlineSave />}
                colorScheme="linkedin"
                onClick={handleSave}
                isLoading={saving}>
                Save
              </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={localSpecification.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Specification name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Input
                  value={localSpecification.status || ""}
                  onChange={(e) => handleChange("status", e.target.value)}
                  placeholder="e.g., draft, active, archived"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Collection</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY003"
                  value={
                    localSpecification.codifyCollection
                      ? { code: localSpecification.codifyCollection, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("codifyCollection", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Standard</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY001"
                  value={
                    localSpecification.dataStandard
                      ? { code: localSpecification.dataStandard, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataStandard", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Type</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY002"
                  value={
                    localSpecification.dataType
                      ? { code: localSpecification.dataType, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataType", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model Provider</FormLabel>
                <Input
                  value={localSpecification.modelProvider || ""}
                  onChange={(e) => handleChange("modelProvider", e.target.value)}
                  placeholder="e.g., OpenAI, Anthropic"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY006"
                  value={
                    localSpecification.model
                      ? { code: localSpecification.model, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("model", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Owner Party ID</FormLabel>
                <Input
                  value={localSpecification.ownerPartyId || ""}
                  onChange={(e) => handleChange("ownerPartyId", e.target.value)}
                  placeholder="Owner party ID"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Is Default</FormLabel>
                <Switch
                  isChecked={localSpecification.isDefault || false}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl mt={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={localSpecification.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Description of this specification"
                rows={4}
              />
            </FormControl>

            {!isNew && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                <FormControl>
                  <FormLabel>Created By</FormLabel>
                  <Input value={localSpecification.createdBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Created Date</FormLabel>
                  <Input
                    value={
                      localSpecification.createdDate
                        ? new Date(localSpecification.createdDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated By</FormLabel>
                  <Input value={localSpecification.updatedBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated Date</FormLabel>
                  <Input
                    value={
                      localSpecification.updatedDate
                        ? new Date(localSpecification.updatedDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>
              </SimpleGrid>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CodifySpecificationsEditor;
